// src/utils/quantityTiers.js — Maxula
// ============================================================================
// PRIX DEGRESSIF — CALCUL COTE CLIENT
// AbodyStudio Limited — https://abodystudio.com
//
// Ce fichier reproduit EXACTEMENT `server/utils/quantityTiers.js`. Le serveur
// reste seul juge du prix facture ; ce calcul ne sert qu'a AFFICHER le bon
// montant dans le panier et au paiement.
//
// Les deux doivent donner le meme resultat au centime pres. Un ecart entre le
// total affiche et le total debite est la pire chose sur une page de paiement :
// le client abandonne, et il a raison de se mefier.
//
// Regles identiques au serveur :
//   - palier retenu = plus grande minQty atteinte
//   - egalite de minQty : la remise la plus avantageuse gagne
//   - minQty < 2 ignore, remise hors ]0 ; 90] ignoree
//   - arrondi au centime sur le PRIX UNITAIRE, jamais sur le total
// ============================================================================

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export function sanitizeQuantityTiers(input) {
  let arr = input;
  if (typeof arr === 'string') {
    try { arr = JSON.parse(arr); } catch (e) { return []; }
  }
  if (!Array.isArray(arr)) return [];

  const byQty = new Map();
  for (const raw of arr) {
    if (!raw || typeof raw !== 'object') continue;
    const minQty = Math.floor(Number(raw.minQty ?? raw.qty));
    const pct = Number(raw.discountPercent ?? raw.percent);
    if (!Number.isFinite(minQty) || minQty < 2 || minQty > 1000) continue;
    if (!Number.isFinite(pct) || pct <= 0 || pct > 90) continue;
    const prev = byQty.get(minQty);
    if (prev == null || pct > prev) byQty.set(minQty, pct);
  }

  return [...byQty.entries()]
    .map(([minQty, discountPercent]) => ({ minQty, discountPercent: round2(discountPercent) }))
    .sort((a, b) => a.minQty - b.minQty)
    .slice(0, 5);
}

export function tierForQuantity(tiers, qty) {
  const q = Math.floor(Number(qty) || 0);
  const list = sanitizeQuantityTiers(tiers);
  let best = null;
  for (const t of list) {
    if (q >= t.minQty && (!best || t.minQty > best.minQty)) best = t;
  }
  return best;
}

/**
 * Prix unitaire apres remise de palier.
 * @param {number} basePrice prix unitaire hors palier (variante comprise)
 * @param {Array}  tiers
 * @param {number} qty
 */
export function unitPriceWithTiers(basePrice, tiers, qty) {
  const base = Number(basePrice) || 0;
  if (base <= 0) return { price: base, tier: null };
  const tier = tierForQuantity(tiers, qty);
  if (!tier) return { price: base, tier: null };
  return { price: round2(base * (1 - tier.discountPercent / 100)), tier };
}

/** Prix unitaire effectif d'une ligne de panier. */
export function cartItemUnitPrice(item) {
  const base = Number(item?.basePrice ?? item?.price ?? 0) || 0;
  return unitPriceWithTiers(base, item?.quantityTiers, item?.quantity).price;
}

/** Total d'une ligne de panier. */
export function cartItemTotal(item) {
  return round2(cartItemUnitPrice(item) * (Number(item?.quantity) || 0));
}

export { round2 };
